import type { ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { mediaUrl } from '../api/client';
import { usePolls, useUpdatePoll } from '../api/queries';
import type { AdminPoll } from '../api/types';
import ui from '../components/ui.module.css';
import { errorMessage, ptNumber, sinceLabel } from '../copy';
import styles from './PollsList.module.css';

/**
 * Every poll the deployment has, closed ones included — `GET /admin/polls`, the
 * panel's own list, which is why the voter-facing ordering and filters on the
 * public endpoints are untouched (02-requirements.md §Poll Administration).
 */
export function PollsList(): ReactNode {
  const navigate = useNavigate();
  const { data: polls, isLoading, error } = usePolls();

  return (
    <section className={ui.panel}>
      <div className={styles.header}>
        <h1 className={ui.h1}>Enquetes</h1>
        <button type="button" className={ui.primary} onClick={() => void navigate('/enquetes/nova')}>
          Nova enquete
        </button>
      </div>

      {error ? <p className={ui.error}>{errorMessage(error)}</p> : null}
      {isLoading ? <p className={ui.muted}>Carregando…</p> : null}

      {polls && polls.length === 0 ? (
        <p className={ui.muted}>Nenhuma enquete ainda. Crie a primeira.</p>
      ) : null}

      {polls && polls.length > 0 ? (
        <table className={styles.table}>
          <thead>
            <tr>
              <th />
              <th>Enquete</th>
              <th>Categoria</th>
              <th className={styles.numeric}>Opções</th>
              <th className={styles.numeric}>Votos</th>
              <th>Situação</th>
            </tr>
          </thead>
          <tbody>
            {polls.map((poll) => (
              <tr key={poll.id}>
                <td>
                  {poll.cover_image ? (
                    <img className={ui.thumb} src={mediaUrl(poll.cover_image)} alt="" />
                  ) : (
                    <div className={ui.thumb} />
                  )}
                </td>
                <td>
                  <button
                    type="button"
                    className={styles.title}
                    onClick={() => void navigate(`/enquetes/${poll.id}`)}
                  >
                    {poll.title}
                  </button>
                  {/* A poll has one date, the day it opened — it never closes on
                      its own (§Perpetual Polls), so there is no end to print. */}
                  <span className={styles.since}>{sinceLabel(poll.created_at)}</span>
                </td>
                {/* Todas as categorias, a principal primeiro — é a ordem que a
                    API devolve (§Poll Categories). `category` sozinha só
                    aparece para uma enquete que ainda não veio com a lista. */}
                <td>
                  {poll.tags?.length ? poll.tags.map((tag) => tag.name).join(' • ') : (poll.category ?? '—')}
                </td>
                <td className={styles.numeric}>{poll.candidate_count}</td>
                <td className={styles.numeric}>{ptNumber(poll.total_votes)}</td>
                <td>
                  <div className={styles.status}>
                    {poll.paused ? (
                      <span className={`${ui.badge} ${ui.badgePaused}`}>PAUSADA</span>
                    ) : (
                      <span
                        className={
                          poll.status === 'active'
                            ? `${ui.badge} ${ui.badgeActive}`
                            : `${ui.badge} ${ui.badgeClosed}`
                        }
                      >
                        {poll.status === 'active' ? 'ABERTA' : 'FECHADA'}
                      </span>
                    )}
                    {poll.political ? (
                      <span
                        className={`${ui.badge} ${poll.electoral_blocked ? ui.badgePaused : ui.badgeClosed}`}
                        title={
                          poll.electoral_blocked
                            ? 'Fora do ar pelo período eleitoral'
                            : 'Enquete política: regras eleitorais e LGPD'
                        }
                      >
                        {poll.electoral_blocked ? 'VEDADA' : 'POLÍTICA'}
                      </span>
                    ) : null}
                    <PauseToggle poll={poll} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </section>
  );
}

/**
 * Pause / resume in one click. A paused poll disappears from the app — lists,
 * feed, rankings, detail — and takes no votes; resuming brings it back with its
 * status and every vote intact. One component per row so each owns its mutation.
 */
export function PauseToggle({ poll }: { poll: Pick<AdminPoll, 'id' | 'title' | 'paused'> }): ReactNode {
  const update = useUpdatePoll(poll.id);
  return (
    <>
      <button
        type="button"
        className={`${ui.ghost} ${ui.small}`}
        disabled={update.isPending}
        aria-label={poll.paused ? `Retomar ${poll.title}` : `Pausar ${poll.title}`}
        onClick={() => update.mutate({ paused: !poll.paused })}
      >
        {poll.paused ? '▶ Retomar' : '❚❚ Pausar'}
      </button>
      {update.error ? <span className={ui.muted}>{errorMessage(update.error)}</span> : null}
    </>
  );
}
